import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, TrendingUp, MousePointerClick, Eye, Percent } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

type Row = { keys: string[]; clicks: number; impressions: number; ctr: number; position: number };

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 28 days", days: 28 },
  { label: "Last 90 days", days: 90 },
];

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

async function callGsc(action: "sites" | "query", body?: unknown) {
  const { data, error } = await supabase.functions.invoke(`gsc-performance?action=${action}`, {
    body: body ?? {},
  });
  if (error) throw error;
  return data;
}

export default function GscPerformanceTab() {
  const [sites, setSites] = useState<string[]>([]);
  const [site, setSite] = useState<string>("");
  const [days, setDays] = useState(28);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trend, setTrend] = useState<Row[]>([]);
  const [pages, setPages] = useState<Row[]>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [pageTrend, setPageTrend] = useState<Row[]>([]);

  // Load sites
  useEffect(() => {
    (async () => {
      try {
        const data = await callGsc("sites");
        const list = (data?.siteEntry ?? []).map((s: { siteUrl: string }) => s.siteUrl);
        setSites(list);
        if (list.length) setSite(list[0]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load sites");
      }
    })();
  }, []);

  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    end.setDate(end.getDate() - 2); // GSC has ~2 day delay
    const start = new Date(end);
    start.setDate(start.getDate() - days);
    return { startDate: fmtDate(start), endDate: fmtDate(end) };
  }, [days]);

  const loadData = async () => {
    if (!site) return;
    setLoading(true);
    setError(null);
    setSelectedPage(null);
    setPageTrend([]);
    try {
      const [byDate, byPage] = await Promise.all([
        callGsc("query", { site, startDate, endDate, dimensions: ["date"], rowLimit: 1000 }),
        callGsc("query", { site, startDate, endDate, dimensions: ["page"], rowLimit: 50 }),
      ]);
      setTrend((byDate?.rows ?? []) as Row[]);
      setPages((byPage?.rows ?? []) as Row[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (site) loadData(); /* eslint-disable-next-line */ }, [site, days]);

  const loadPageTrend = async (pageUrl: string) => {
    setSelectedPage(pageUrl);
    setPageTrend([]);
    try {
      const data = await callGsc("query", {
        site, startDate, endDate, dimensions: ["date"], page: pageUrl, rowLimit: 1000,
      });
      setPageTrend((data?.rows ?? []) as Row[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load page trend");
    }
  };

  const totals = useMemo(() => {
    const t = trend.reduce(
      (acc, r) => ({
        clicks: acc.clicks + r.clicks,
        impressions: acc.impressions + r.impressions,
      }),
      { clicks: 0, impressions: 0 },
    );
    return { ...t, ctr: t.impressions ? (t.clicks / t.impressions) * 100 : 0 };
  }, [trend]);

  const chartData = (rows: Row[]) =>
    rows.map((r) => ({
      date: r.keys[0],
      clicks: r.clicks,
      impressions: r.impressions,
      ctr: +(r.ctr * 100).toFixed(2),
    }));

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={site}
          onChange={(e) => setSite(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)", color: "hsl(0 0% 90%)" }}
        >
          {sites.length === 0 && <option>Loading sites…</option>}
          {sites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-1 rounded-lg border p-1" style={{ borderColor: "hsl(0 0% 16%)" }}>
          {RANGES.map((r) => (
            <button key={r.days} onClick={() => setDays(r.days)}
              className="px-3 py-1.5 text-xs rounded-md transition-colors"
              style={{
                background: days === r.days ? "hsl(207 68% 50%)" : "transparent",
                color: days === r.days ? "white" : "hsl(0 0% 70%)",
              }}>{r.label}</button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadData} disabled={loading}
          className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
          {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border p-3 text-sm" style={{ borderColor: "hsl(0 70% 40%)", background: "hsl(0 70% 20% / 0.2)", color: "hsl(0 70% 80%)" }}>
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={<Eye size={16} />} label="Impressions" value={totals.impressions.toLocaleString()} />
        <StatCard icon={<MousePointerClick size={16} />} label="Clicks" value={totals.clicks.toLocaleString()} />
        <StatCard icon={<Percent size={16} />} label="Avg. CTR" value={`${totals.ctr.toFixed(2)}%`} />
      </div>

      {/* Trend chart */}
      <Panel title="Site performance trend" subtitle={`${startDate} → ${endDate}`}>
        {loading ? <Skeleton /> : trend.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData(trend)}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(0 0% 18%)" />
              <XAxis dataKey="date" stroke="hsl(0 0% 50%)" fontSize={11} />
              <YAxis yAxisId="left" stroke="hsl(0 0% 50%)" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="hsl(0 0% 50%)" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(0 0% 8%)", border: "1px solid hsl(0 0% 18%)", borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="hsl(207 68% 60%)" dot={false} strokeWidth={2} />
              <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="hsl(142 70% 50%)" dot={false} strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="hsl(35 90% 60%)" dot={false} strokeWidth={2} name="CTR %" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>

      {/* Pages table */}
      <Panel title="Top pages" subtitle="Click a page to see its trend">
        {loading ? <Skeleton /> : pages.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "hsl(0 0% 55%)" }} className="text-left">
                  <th className="py-2 pr-3 font-medium">Page</th>
                  <th className="py-2 px-3 font-medium text-right">Impressions</th>
                  <th className="py-2 px-3 font-medium text-right">Clicks</th>
                  <th className="py-2 px-3 font-medium text-right">CTR</th>
                  <th className="py-2 pl-3 font-medium text-right">Position</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((r) => {
                  const url = r.keys[0];
                  const active = selectedPage === url;
                  return (
                    <tr key={url} onClick={() => loadPageTrend(url)}
                      className="cursor-pointer border-t transition-colors hover:bg-[hsl(0_0%_10%)]"
                      style={{ borderColor: "hsl(0 0% 14%)", background: active ? "hsl(207 68% 50% / 0.08)" : "transparent" }}>
                      <td className="py-2 pr-3 max-w-md truncate" style={{ color: "hsl(0 0% 85%)" }}>
                        {url.replace(site, "/") || "/"}
                      </td>
                      <td className="py-2 px-3 text-right" style={{ color: "hsl(0 0% 80%)" }}>{r.impressions.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right" style={{ color: "hsl(0 0% 80%)" }}>{r.clicks.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right" style={{ color: "hsl(0 0% 80%)" }}>{(r.ctr * 100).toFixed(2)}%</td>
                      <td className="py-2 pl-3 text-right" style={{ color: "hsl(0 0% 80%)" }}>{r.position.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Per-page trend */}
      {selectedPage && (
        <Panel title="Page trend" subtitle={selectedPage}>
          {pageTrend.length === 0 ? <Skeleton /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData(pageTrend)}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(0 0% 18%)" />
                <XAxis dataKey="date" stroke="hsl(0 0% 50%)" fontSize={11} />
                <YAxis yAxisId="left" stroke="hsl(0 0% 50%)" fontSize={11} />
                <YAxis yAxisId="right" orientation="right" stroke="hsl(0 0% 50%)" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(0 0% 8%)", border: "1px solid hsl(0 0% 18%)", borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="hsl(207 68% 60%)" dot={false} strokeWidth={2} />
                <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="hsl(142 70% 50%)" dot={false} strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="hsl(35 90% 60%)" dot={false} strokeWidth={2} name="CTR %" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Panel>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
      <p className="flex items-center gap-2 text-sm" style={{ color: "hsl(0 0% 55%)" }}>{icon} {label}</p>
      <p className="mt-1 text-2xl font-bold" style={{ color: "hsl(0 0% 95%)" }}>{value}</p>
    </div>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
      <div className="mb-4">
        <h3 className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>{title}</h3>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: "hsl(0 0% 50%)" }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Skeleton() {
  return <div className="h-[260px] flex items-center justify-center" style={{ color: "hsl(0 0% 40%)" }}>
    <Loader2 size={18} className="animate-spin mr-2" /> Loading…
  </div>;
}
function Empty() {
  return <div className="h-[120px] flex items-center justify-center text-sm" style={{ color: "hsl(0 0% 50%)" }}>
    <TrendingUp size={16} className="mr-2" /> No data available for this range.
  </div>;
}
